import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, Tooltip as ReTooltip,
} from 'recharts';
import TapTooltipChart from '@/components/TapTooltipChart';
import { AlertTriangle, TrendingUp, TrendingDown, Minus, Loader2, HelpCircle, ChevronDown, ChevronUp, Sparkles, Info, ShieldAlert, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import AbbrText from '@/components/AbbrText';
import InlinePopover from '@/components/InlinePopover';
import ReitTriangleRibbon from '@/components/ReitTriangleRibbon';
import DividripOpinionBanner from '@/components/DividripOpinionBanner';
import BuyChecklist from '@/components/BuyChecklist';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Dividend Triangle Scorecard
// ---------------------------
// 3-axis radar chart of 5-year CAGR for Revenue, EPS, and Dividends.
// Uses Recharts so it themes naturally and is interactive on mobile.
// Backend caches 24h in triangle_scorecard_cache. The Triangle Score
// is the average of the 3 normalized axes (each 0-100). A "Dividend
// Trap" flag fires when dividend growth materially outpaces earnings.
//
// Adaptive window: if a company has only 3 years of data, we show
// "n=3yr" instead of "5yr" — newer tickers (CDIV, MTCH) still get
// scored rather than blanked.
//
// Props:
//   ticker          — stock ticker (required)
//   isDark          — boolean for theme-aware fill
//   compact         — true to render in a tighter layout (modal tab)
//   onOpenStandalone — () => void. Optional CTA to open the
//                     /triangle/:ticker shareable route.

function cagrTone(cagr) {
  if (cagr === null || cagr === undefined) return 'text-muted-foreground';
  if (cagr >= 8) return 'text-emerald-600 dark:text-emerald-400';
  if (cagr >= 3) return 'text-blue-600 dark:text-blue-400';
  if (cagr >= 0) return 'text-amber-600 dark:text-amber-400';
  return 'text-rose-600 dark:text-rose-400';
}

function CagrIcon({ cagr, className = 'w-4 h-4' }) {
  if (cagr === null || cagr === undefined) return <Minus className={`${className} text-muted-foreground`} />;
  if (cagr >= 0) return <TrendingUp className={`${className} ${cagrTone(cagr)}`} />;
  return <TrendingDown className={`${className} ${cagrTone(cagr)}`} />;
}

function fmtPct(n) {
  if (n === null || n === undefined) return '—';
  const sign = n >= 0 ? '+' : '';
  return `${sign}${Number(n).toFixed(1)}%`;
}

function scoreTone(score, isEtf) {
  if (score === null || score === undefined) return 'text-muted-foreground';
  // Round 92j — ETFs only get a single-leg (Dividend) score, so the
  // red/amber bands designed for full 3-leg stock scores are misleading.
  // For ETFs we always render a neutral blue, regardless of score value.
  if (isEtf) return 'text-sky-600 dark:text-sky-400';
  if (score >= 70) return 'text-emerald-600 dark:text-emerald-400';
  if (score >= 50) return 'text-blue-600 dark:text-blue-400';
  if (score >= 35) return 'text-amber-600 dark:text-amber-400';
  return 'text-rose-600 dark:text-rose-400';
}

function scoreVerdict(score, isEtf) {
  if (score === null || score === undefined) return 'Insufficient data';
  // Round 92j — ETFs always get the distribution-only label because
  // the underlying Triangle math doesn't apply (no revenue/EPS legs).
  // Steers users to the right tabs for ETF judgement.
  if (isEtf) return 'Distribution-only score · use Div Growth + Fund Profile';
  if (score >= 75) return 'Strong dividend triangle';
  if (score >= 60) return 'Healthy growth profile';
  if (score >= 45) return 'Mixed signals';
  if (score >= 30) return 'Weak fundamentals';
  return 'High risk of cut';
}

function MetricTile({ label, cagr, years, footnote, popoverContent }) {
  return (
    <div
      className="rounded-lg border bg-card p-3 sm:p-4"
      data-testid={`triangle-tile-${label.toLowerCase()}`}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
            {label}
          </span>
          {popoverContent && (
            <InlinePopover
              testId={`triangle-tile-help-${label.toLowerCase()}`}
              maxWidth={280}
              ariaLabel={`What is ${label} CAGR`}
              className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full text-muted-foreground/80 hover:text-blue-500 hover:bg-blue-500/10 transition-colors align-middle"
              render={() => popoverContent}
            >
              <HelpCircle className="w-3 h-3" />
            </InlinePopover>
          )}
        </div>
        <CagrIcon cagr={cagr} />
      </div>
      <div className={`text-2xl font-bold font-mono ${cagrTone(cagr)}`}>
        {fmtPct(cagr)}
      </div>
      <div className="text-[10px] text-muted-foreground mt-0.5">
        {years > 0 ? `n=${years}yr CAGR` : 'no data'}
        {footnote ? ` · ${footnote}` : ''}
      </div>
    </div>
  );
}

export default function TriangleScorecard({ ticker, isDark, compact = false, onOpenStandalone, onDataLoaded, onOpenStockModal, onOpenExecutiveSummary }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [whyOpen, setWhyOpen] = useState(true);
  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    axios.get(`${API}/stocks/${ticker}/triangle`, { withCredentials: true })
      .then((r) => {
        if (!cancelled) {
          setData(r.data);
          if (onDataLoaded) onDataLoaded(r.data);
        }
      })
      .catch((e) => {
        if (!cancelled) setErr(e?.response?.data?.detail || 'Could not load triangle data.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticker]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="triangle-loading">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading Triangle Scorecard…</span>
      </div>
    );
  }

  if (err || !data) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-muted/20 p-6 text-center" data-testid="triangle-error">
        <p className="text-sm text-muted-foreground">{err || 'No triangle data available for this ticker yet.'}</p>
      </div>
    );
  }

  const { revenue, eps, dividend, radar, triangle_score, trap_flag, trap_reason, trap_override, narrative, is_reit, is_mlp, is_etf, opinion, chowder, checklist } = data;

  // Recharts expects an array of objects with one entry per axis.
  const radarData = [
    { metric: 'Revenue', value: radar?.revenue ?? 0, cagr: revenue?.cagr_pct },
    { metric: 'EPS', value: radar?.eps ?? 0, cagr: eps?.cagr_pct },
    { metric: 'Dividend', value: radar?.dividend ?? 0, cagr: dividend?.cagr_pct },
  ];

  // Color of the radar fill — emerald for healthy, amber for mixed, rose for risk.
  // Round 92j — ETFs get a neutral sky color since the red/amber bands
  // don't apply to single-leg distribution-only scores.
  const score = triangle_score;
  let radarStroke = '#10b981'; // emerald-500
  let radarFill = 'rgba(16, 185, 129, 0.25)';
  if (is_etf) {
    radarStroke = '#0284c7'; // sky-600
    radarFill = 'rgba(2, 132, 199, 0.20)';
  } else if (score !== null && score !== undefined) {
    if (score < 50) { radarStroke = '#f59e0b'; radarFill = 'rgba(245, 158, 11, 0.25)'; }
    if (score < 35) { radarStroke = '#ef4444'; radarFill = 'rgba(239, 68, 68, 0.25)'; }
  }

  return (
    <div className="space-y-4 pb-24 sm:pb-4" data-testid="triangle-scorecard">
      {/* REIT-aware nudge — only renders when is_reit is true. When
          we're in `compact` mode (rendered inside the Stock Modal)
          the ribbon switches its copy to "on the Stock Metrics Tab"
          since the user is already inside the modal — telling them
          to open a modal they're inside would be nonsensical. */}
      <ReitTriangleRibbon
        isReit={is_reit}
        isMlp={is_mlp}
        onOpenStockModal={onOpenStockModal}
        insideModal={compact}
      />

      {/* Composite score header */}
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
            Dividend Triangle Score
          </div>
          <div className={`text-3xl sm:text-4xl font-bold font-mono ${scoreTone(score, is_etf)}`} data-testid="triangle-score-value">
            {score !== null && score !== undefined ? `${score}/100` : '—'}
          </div>
          <div className="text-xs text-muted-foreground">{scoreVerdict(score, is_etf)}</div>
        </div>
        {onOpenStandalone && (
          <Button variant="outline" size="sm" onClick={onOpenStandalone} data-testid="triangle-open-standalone">
            Open full view
          </Button>
        )}
      </div>

      {/* "Why this score?" collapsible narrative — plain-English bullets
          generated server-side from the cached fundamentals. Open by
          default because most users want the reasoning visible.
          Round 95y (Jun 2026) — moved to render right under the score
          header (above the trap-flag and the Opinion card) so the
          mechanical reasoning shows BEFORE the verdict. */}
      {Array.isArray(narrative) && narrative.length > 0 && (
        <div className="rounded-lg border bg-card" data-testid="triangle-why-panel">
          <button
            type="button"
            onClick={() => setWhyOpen((v) => !v)}
            className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-muted/40 transition-colors rounded-lg"
            data-testid="triangle-why-toggle"
            aria-expanded={whyOpen}
          >
            <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              <Sparkles className="w-3.5 h-3.5 text-violet-500" />
              Why this score?
              <span className="text-[10px] font-normal text-muted-foreground/70 normal-case tracking-normal">
                ({narrative.length} {narrative.length === 1 ? 'insight' : 'insights'})
              </span>
            </span>
            {whyOpen ? (
              <ChevronUp className="w-4 h-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            )}
          </button>
          {whyOpen && (
            <ul className="px-3 pb-3 pt-1 space-y-1.5" data-testid="triangle-why-list">
              {narrative.map((item, idx) => {
                const tone = item?.tone || 'info';
                const TONE_STYLE = {
                  positive: 'border-emerald-300/60 bg-emerald-50/60 dark:bg-emerald-950/30 dark:border-emerald-800/50 text-emerald-900 dark:text-emerald-100',
                  caution: 'border-amber-300/60 bg-amber-50/60 dark:bg-amber-950/30 dark:border-amber-800/50 text-amber-900 dark:text-amber-100',
                  negative: 'border-rose-300/70 bg-rose-50/60 dark:bg-rose-950/30 dark:border-rose-800/50 text-rose-900 dark:text-rose-100',
                  info: 'border-sky-300/60 bg-sky-50/40 dark:bg-sky-950/20 dark:border-sky-800/50 text-sky-900 dark:text-sky-100',
                }[tone] || '';
                const TONE_ICON = {
                  positive: <TrendingUp className="w-3.5 h-3.5 shrink-0 mt-0.5 text-emerald-600 dark:text-emerald-400" />,
                  caution: <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />,
                  negative: <ShieldAlert className="w-3.5 h-3.5 shrink-0 mt-0.5 text-rose-600 dark:text-rose-400" />,
                  info: <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-sky-600 dark:text-sky-400" />,
                }[tone];
                return (
                  <li
                    key={idx}
                    className={`flex items-start gap-2 rounded-md border px-2.5 py-2 text-xs leading-snug ${TONE_STYLE}`}
                    data-testid={`triangle-why-item-${idx}`}
                  >
                    {TONE_ICON}
                    <AbbrText text={item?.text} as="span" />
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {trap_override && !is_etf && (
        <div
          className="flex items-start gap-2 rounded-lg border border-emerald-300/70 bg-emerald-50/60 dark:bg-emerald-950/30 dark:border-emerald-800/50 p-3"
          data-testid="triangle-trap-override"
        >
          <ShieldCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
          <div className="text-xs text-emerald-900 dark:text-emerald-200">
            <span className="font-semibold">Trap signal overridden by cash coverage.</span>{' '}
            Free cash flow covers the dividend <span className="font-mono font-bold" data-testid="triangle-trap-override-coverage">{trap_override.fcf_coverage}×</span> (threshold {trap_override.threshold}×), so the growth-rate signal is treated as a cyclical artifact rather than a trap. Original signal: <AbbrText text={trap_override.original_reason} as="span" />
          </div>
        </div>
      )}

      {/* Dividend trap warning — suppressed for ETFs since the trap
          detector relies on the EPS leg which ETFs don't have. */}
      {trap_flag && !is_etf && (
        <div
          className="flex items-start gap-2 rounded-lg border border-rose-300/70 bg-rose-50/60 dark:bg-rose-950/30 dark:border-rose-800/50 p-3"
          data-testid="triangle-trap-warning"
        >
          <AlertTriangle className="w-4 h-4 text-rose-600 dark:text-rose-400 shrink-0 mt-0.5" />
          <div className="text-xs text-rose-800 dark:text-rose-200">
            <span className="font-semibold">Potential Dividend Trap.</span>{' '}
            <AbbrText text={trap_reason} as="span" />
          </div>
        </div>
      )}

      {/* DiviDrip's Buy Checklist — asset-class-aware 5-point gate with
          a Chowder Score header. Round 95z (Jun 2026). Renders between
          the trap-flag warning and the Opinion banner so the user sees
          the mechanical evidence right before reading our verdict. */}
      <BuyChecklist
        chowder={chowder}
        checklist={checklist}
        compact={compact}
        ticker={ticker}
        onOpenExecutiveSummary={onOpenExecutiveSummary}
      />

      {/* DiviDrip's Opinion — final verdict, rendered LAST among the
          banners (just before the radar) so the user reads the
          mechanical evidence (Why this score, Trap flag, Buy
          Checklist) FIRST and arrives at the verdict with that
          context. Round 95y. */}
      {opinion && <DividripOpinionBanner opinion={opinion} compact={compact} />}

      {/* Radar chart. Round 95ae (Jun 2026) — in the PDF export the
          radar was getting sliced at the page boundary (sometimes the
          black card header on page 1, sometimes a metric card cut in
          half). We mark this block as a forced page-break-before so
          the PDF renderer (html2pdf.js, css mode) always starts the
          radar fresh at the top of page 2. `page-break-inside: avoid`
          additionally protects the chart itself from being chopped.
          Browser viewports ignore these properties — only the PDF
          pagination engine consumes them. */}
      <div
        className="rounded-lg border bg-card p-3 pdf-page-break-before"
        style={{
          height: compact ? 260 : 320,
          pageBreakBefore: 'always',
          breakBefore: 'page',
          pageBreakInside: 'avoid',
          breakInside: 'avoid',
        }}
        data-testid="triangle-radar-chart"
      >
        <TapTooltipChart className="h-full">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={radarData} outerRadius="75%">
            <PolarGrid stroke={isDark ? '#374151' : '#e5e7eb'} />
            <PolarAngleAxis
              dataKey="metric"
              tick={{ fill: isDark ? '#d1d5db' : '#374151', fontSize: 12, fontWeight: 600 }}
            />
            <PolarRadiusAxis
              angle={90}
              domain={[0, 100]}
              tick={{ fill: isDark ? '#6b7280' : '#9ca3af', fontSize: 10 }}
              tickCount={5}
            />
            <Radar
              name="5-yr CAGR"
              dataKey="value"
              stroke={radarStroke}
              fill={radarFill}
              strokeWidth={2}
              isAnimationActive
            />
            <ReTooltip
              contentStyle={{
                backgroundColor: isDark ? '#1f2937' : '#fff',
                border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
                borderRadius: 8,
                fontSize: 12,
              }}
              formatter={(_, __, item) => {
                const cagr = item?.payload?.cagr;
                return [fmtPct(cagr), `${item?.payload?.metric} CAGR`];
              }}
              labelFormatter={() => ''}
            />
          </RadarChart>
        </ResponsiveContainer>
        </TapTooltipChart>
      </div>

      {/* Metric tiles. Round 95ae — protect each tile from being
          split across pages in the PDF (the bottom Dividend tile was
          getting chopped before this guard). */}
      <div
        className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3"
        style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}
      >
        <MetricTile
          label="Revenue"
          cagr={revenue?.cagr_pct}
          years={revenue?.years || 0}
          footnote={revenue?.source === 'ttm' ? 'TTM' : ''}
          popoverContent={
            <div className="text-xs leading-relaxed">
              <p className="font-semibold mb-1">Revenue CAGR</p>
              <p>Annual compound growth of the company's top-line sales over the last {revenue?.years || 5} years. Healthy dividend payers usually grow revenue 3%+ per year.</p>
              {revenue?.source === 'ttm' && (
                <p className="mt-1 text-muted-foreground">⚡ Trailing-twelve-month (1-yr) growth from our cached fundamentals — used because a full multi-year income statement wasn't available.</p>
              )}
            </div>
          }
        />
        <MetricTile
          label="EPS"
          cagr={eps?.cagr_pct}
          years={eps?.years || 0}
          footnote={eps?.source === 'ttm' ? 'TTM' : ''}
          popoverContent={
            <div className="text-xs leading-relaxed">
              <p className="font-semibold mb-1">EPS CAGR</p>
              <p>Annual compound growth of diluted earnings per share. EPS should grow at least as fast as the dividend — when it doesn't, the payout ratio rises and a cut becomes more likely.</p>
              {eps?.source === 'ttm' && (
                <p className="mt-1 text-muted-foreground">⚡ Trailing-twelve-month (1-yr) growth from our cached fundamentals — used because a full multi-year EPS history wasn't available.</p>
              )}
            </div>
          }
        />
        <MetricTile
          label="Dividend"
          cagr={dividend?.cagr_pct}
          years={dividend?.years || 0}
          popoverContent={
            <div className="text-xs leading-relaxed">
              <p className="font-semibold mb-1">Dividend CAGR</p>
              <p>Annual compound growth of total dividends paid per share. A growing dividend is only meaningful if Revenue and EPS support it.</p>
              {/* Only attach the Trap/Weak callout when this ticker
                  actually IS in that band AND isn't an ETF — otherwise
                  the popover reads "definition of trap" on a healthy
                  89/100 stock OR on a single-leg ETF where the math
                  doesn't apply. */}
              {!is_etf && (trap_flag || (typeof triangle_score === 'number' && triangle_score < 35)) && (
                <p className="mt-1.5 pt-1.5 border-t border-border/40">
                  <span className="font-semibold text-rose-700 dark:text-rose-300">⚠ Trap / Weak (under 35).</span>{' '}
                  Either a low composite score OR the dividend is growing materially faster than Revenue or EPS — classic dividend-trap signature.
                  {trap_flag ? ' The ⚠ icon means our trap detector fired.' : ''}
                  {' '}Always pair with the Quality Score before buying.
                </p>
              )}
            </div>
          }
        />
      </div>

      <p className="text-[10px] text-muted-foreground text-center" data-testid="triangle-source">
        Source: Massive.com · Cached 24h ·
        Dividend Triangle is for educational use, not investment advice.
      </p>
    </div>
  );
}
